// web-app/src/utils/runDragMath.js
//
// Pure math behind the two live run gestures (drag a run's end, drag an
// appliance along its run) and the ghost preview both draw. No React, no
// room store: a hook decides WHEN; this decides WHAT.
// See reference-docs/layout-relationships/overview.md §3.3.

import {
  worldToWallParam,
  wallParamToSurfacePoint,
  getWallAxes,
} from '@shared-lib/kitchen-assembly/wallMath';
import { synthesizeVirtualWall } from '@shared-lib/cabinet-runs/freestanding';
import {
  moveApplianceObjectsToBays,
  translateFreeSegmentAppliances,
} from '@shared-lib/cabinet-runs/moveAppliances';
import {
  applianceBays,
  baysForRun,
  bayAt,
  legacyApplianceFromBay,
} from '@shared-lib/cabinet-runs/bays';
import {
  overridesForRun,
  isPositionedOverride,
} from '@shared-lib/cabinet-runs/bayOverrides';
import {
  rehydrateRun,
  materializeRunObjects,
  clampApplianceCenterParam,
  bayCenterSpan,
  isStaleDerivedRun,
} from './cabinetRunGeometry';
import { runEndNeighbourReach } from './runEndNeighbours';
import { runEndFillCover } from './runEndFillReach';

const IN = 0.0254;
// Containment tolerance for "does the run still reach around this bay" — a
// micron, so a bay sitting flush with the end just dragged to it is kept
// rather than rounded out by the float error of getting there.
const REACH_EPS = 1e-6;
export const MIN_RUN_SPAN = 12 * IN;
export const PREVIEW_PARAM_QUANTUM = 1 * IN;
const MOVE_EPS_M = 1e-4; // below this a bay did not move

/**
 * The param frame a run end slides in: a wall, the params of the segment's
 * own two ends on it, and whether that axis has hard limits.
 *
 * A wall-backed segment borrows its wall's frame. A FREE-STANDING leg (an
 * island, a peninsula arm) is given the very virtual wall the fill builds
 * from its two endpoints (`synthesizeVirtualWall`, freestanding.js) — the
 * same one, cook side and all, so a bay's `anchorParam` means the same
 * thing to the drag as it does to the fill. Its params are read by
 * PROJECTING the stored endpoints rather than assumed to be `0..length`:
 * the synthesized wall flips end-for-end depending on the cook side, and
 * an assumed origin would mirror every bay clamp on half of them.
 *
 * `bounded` is what a wall has and a free-standing leg does not — an end
 * of the wall to stop at. An island's line is as long as the user draws
 * it, so its frame runs to infinity and only the minimum span and its own
 * bays limit the drag.
 *
 * @returns {{wall, startParam, endParam, wallLen, bounded, freestanding}|null}
 */
export function segmentFrame(seg) {
  if (!seg) return null;
  if (!seg.freestanding) {
    if (!seg.wall) return null;
    const axes = getWallAxes(seg.wall);
    return {
      wall: seg.wall,
      startParam: seg.startParam,
      endParam: seg.endParam,
      wallLen: axes ? axes.wallLen : Infinity,
      bounded: true,
      freestanding: false,
    };
  }
  const { start, end } = seg;
  if (!start || !end) return null;
  const wall = synthesizeVirtualWall(start, end, {
    side: seg.cookSide || 'right',
    id: seg.id || 'freestanding',
  });
  const startParam = worldToWallParam(wall, start);
  const endParam = worldToWallParam(wall, end);
  if (startParam == null || endParam == null) return null;
  return {
    wall,
    startParam,
    endParam,
    wallLen: Infinity,
    bounded: false,
    freestanding: true,
  };
}

/**
 * The segments of a rehydrated run a run end can slide on, in order, each
 * with its frame — a wall-backed segment that resolved a wall, and every
 * free-standing leg.
 */
export function runSegments(run) {
  const out = [];
  (run?.segments || []).forEach((seg, index) => {
    const frame = segmentFrame(seg);
    if (frame) out.push({ seg, frame, index });
  });
  return out;
}

/** The segment a run end belongs to, its frame, and its index in the run. */
export function runEndSegment(run, end) {
  const segs = runSegments(run);
  if (!segs.length) return null;
  return end === 'start' ? segs[0] : segs[segs.length - 1];
}

/**
 * The run's two terminal edges as world points on its own line:
 * `[{ end:'start'|'end', segIndex, wallId, point:{x,z} }]`. Empty only for a
 * run with no segment the drag can resolve a frame for.
 *
 * `rangeOpts` reaches `runEndRange` — pass `{ room, stored }` so an end held
 * by a neighbouring run is drawn the way the drag will treat it.
 */
export function runEndHandles(run, rangeOpts = {}) {
  const first = runEndSegment(run, 'start');
  const last = runEndSegment(run, 'end');
  if (!first || !last) return [];
  const out = [];
  // Where each end is held (`clampRunEnd`): an end saved past the last
  // cabinet is drawn where the cabinets stop, which is where a press on it
  // will put it. Without the room and openings this is the saved end.
  const startPt = wallParamToSurfacePoint(
    first.frame.wall,
    clampRunEnd(run, 'start', first.frame.startParam, rangeOpts)
  );
  const endPt = wallParamToSurfacePoint(
    last.frame.wall,
    clampRunEnd(run, 'end', last.frame.endParam, rangeOpts)
  );
  const pin = (end) => {
    const range = runEndRange(run, end, rangeOpts);
    const pinned = range.hi - range.lo <= PINNED_EPS;
    return { pinned, reason: pinned ? runEndPinReason(range) : null };
  };
  // The line's direction, for a cursor that points along it.
  const dirOf = (frame) => getWallAxes(frame.wall)?.wallDir || { x: 1, z: 0 };
  // Away from the run at this end: the wall's direction only when the run
  // was drawn along it — `start` is not always the lower param (see
  // `runEndRange`). Null for a segment with no length to point along.
  const outwardOf = (frame, end) => {
    const along =
      end === 'end'
        ? frame.endParam - frame.startParam
        : frame.startParam - frame.endParam;
    if (!along) return null;
    const d = dirOf(frame);
    return along > 0 ? d : { x: -d.x, z: -d.z };
  };
  if (startPt)
    out.push({
      end: 'start',
      segIndex: first.index,
      // A free-standing leg is backed by a synthesized wall that exists
      // only for this frame, so it has no room-wall id to report.
      wallId: first.seg.freestanding ? null : first.frame.wall.id,
      point: startPt,
      dir: dirOf(first.frame),
      outward: outwardOf(first.frame, 'start'),
      ...pin('start'),
    });
  if (endPt)
    out.push({
      end: 'end',
      segIndex: last.index,
      wallId: last.seg.freestanding ? null : last.frame.wall.id,
      point: endPt,
      dir: dirOf(last.frame),
      outward: outwardOf(last.frame, 'end'),
      ...pin('end'),
    });
  return out;
}

export function paramAtPoint(wall, point) {
  return worldToWallParam(wall, { x: point.x, z: point.z });
}

export function quantizeParam(p) {
  return Math.round(p / PREVIEW_PARAM_QUANTUM) * PREVIEW_PARAM_QUANTUM;
}

/**
 * Move ONE appliance bay's anchor along its run, clamped: the STORED run with
 * the new `anchorParam`, plus where it actually landed.
 *
 * Shared by the two gestures that move a bay — the 2D drag (`useRunDrag`) and
 * the panel's along-run nudge (`useBayOverrideApply.nudgeBayAlongRun`) — so
 * one clamp decides what a bay may do, whichever way the user asks. The bay's
 * OBJECTS are not touched here: the run re-solve moves the appliance and its
 * fixture group (`moveApplianceObjectsToBays`), which is the whole reason a
 * bay edit is expressed as an anchor rather than a translation.
 *
 * `quantize` is the DRAG's 1" bucket, which exists to memoise the per-frame
 * preview. A nudge must not pass it: its step is already exact, and rounding
 * 0.5" to the nearest inch is how a fine nudge becomes either nothing or 1".
 *
 * @returns {{stored, bay, param, moved}|null} `moved` is false when the clamp
 *   refused to let the bay travel (a neighbouring bay, an opening, the run's
 *   end) — the caller says so rather than reporting a move that didn't happen.
 */
export function editBayAnchor(
  room,
  stored,
  bayId,
  targetParam,
  openings = [],
  { quantize = false } = {}
) {
  if (!stored || !Number.isFinite(targetParam)) return null;
  // Through the migrating reader: a fresh apply persists legacy `appliances[]`
  // and no `bays`, and reading `stored.bays` directly made every bay drag on
  // such a run a silent no-op (found 2026-08-30).
  const allBays = baysForRun(stored);
  const bay = allBays.find((b) => b.id === bayId);
  if (!bay) return null;
  const segIndex = bay.segmentIndex ?? 0;
  // Clamp against everything EXCEPT this bay (it must not refuse itself).
  const without = { ...stored, bays: allBays.filter((b) => b.id !== bay.id) };
  const type = legacyApplianceFromBay(bay).type;
  const clampAt = (p) =>
    clampApplianceCenterParam(without, segIndex, type, p, room, openings, {
      fillOf: stored,
    });
  // A bay may never leave its own segment, whatever the richer clamp says.
  // `clampApplianceCenterParam` returns null when the appliance fits NOWHERE
  // clear — a 27" sink on a 60" island that also holds a dishwasher — and the
  // target was then taken verbatim: on a wall the wall's end still stopped
  // the arrows, but an island has no end, so a drag took the sink off it.
  const span = bayCenterSpan(room, stored, segIndex, bay);
  const toSpan = (p) => (span ? Math.min(Math.max(p, span.lo), span.hi) : p);
  // The POINTER is quantised, never the answer: quantising the clamped
  // param rounded a stop at the dishwasher back by up to half an inch, and
  // left that gap beside it (2026-10-26). A stop is the same exact value for
  // every frame past it, so the preview memo still holds.
  const target = quantize ? quantizeParam(targetParam) : targetParam;
  const clamped = clampAt(target);
  const param = toSpan(clamped != null ? clamped : target);
  // `bayAt`, so a vanity's bowl travels with its sink base: the bay states
  // where the bowl sits as well as where the box stands, and moving only the
  // box left the sink, faucet, mirror and light behind (2026-11-02).
  const bays = allBays.map((b) =>
    b.id === bay.id ? { ...bayAt(b, param), unplaced: false } : b
  );
  // The edit is expressed in BAYS from here on; the stale legacy list must not
  // shadow it on the next read.
  const { appliances: _legacy, ...rest } = stored;
  return {
    stored: { ...rest, bays },
    bay,
    param,
    moved: Math.abs(param - bay.anchorParam) > MOVE_EPS_M,
  };
}

/** Edges this close are touching, not overlapping. */
const NEIGHBOUR_EPS = 1e-4;

/**
 * The spans the OTHER runs in the room hold on each of this run's wall-backed
 * segments' walls, by the run's segment index — what `runEndRange` stops a
 * dragged end at. A derived run with nothing left of it is no neighbour (a
 * kitchen applied over it since, `isStaleDerivedRun`). Params are each run's
 * own stored ones: two runs on one wall are stored on the same room wall.
 *
 * @param {Object} room
 * @param {Object} run a REHYDRATED run
 * @returns {Object<number, Array<{lo: number, hi: number}>>}
 */
export function runNeighbourSpans(room, run) {
  const out = {};
  (run?.segments || []).forEach((seg, i) => {
    if (seg.freestanding || !seg.wall) return;
    const spans = [];
    for (const other of room?.cabinetRuns || []) {
      if (!other || other.id === run.id) continue;
      if (isStaleDerivedRun(other, room?.objects)) continue;
      for (const s of other.segments || []) {
        if (s.freestanding || s.wallId !== seg.wall.id) continue;
        spans.push({
          lo: Math.min(s.startParam, s.endParam),
          hi: Math.max(s.startParam, s.endParam),
        });
      }
    }
    if (spans.length) out[i] = spans;
  });
  return out;
}

/**
 * Where a run end MAY go: `[lo, hi]` in its segment's frame, and what set
 * the binding edge — the wall's end, the minimum span from the far end, the
 * outermost appliance bay on that segment (a run cannot end in the middle of
 * its own sink), or ANOTHER RUN standing in the way. Shared by the clamp and
 * by the handles, so a handle that cannot move is drawn as one and the press
 * says why.
 *
 * A free-standing leg has no wall to stop at, so only the span, its own bays
 * and the runs around it bind it — the same freedom the draw tool gives it.
 *
 * The neighbour bound needs the ROOM (what stands in it) and the stored run
 * (whose objects are its own, aliases included); without `room` it is not
 * applied, so a caller that has the room must pass it.
 *
 * With `openings` as well, the end is held to where the fill can actually put
 * a cabinet (`runEndFillCover`): it grows no further than the last stretch
 * the run's pieces cover with the end pushed out, and `gaps` lists the
 * uncovered stretches inside the range — a doorway the end may cross but not
 * stop in (`clampRunEnd` sends it back to the near edge).
 *
 * @returns {{lo: number, hi: number, boundBy: 'wall'|'span'|'bay'|'run'|'fill', bay: Object|null, gaps: Array}}
 */
export function runEndRange(
  run,
  end,
  {
    minSpan = MIN_RUN_SPAN,
    room = null,
    stored = null,
    neighbours = {},
    openings = null,
  } = {}
) {
  const at = runEndSegment(run, end);
  if (!at) {
    return {
      lo: -Infinity,
      hi: Infinity,
      loBy: 'wall',
      hiBy: 'wall',
      loBay: null,
      hiBay: null,
      boundBy: 'wall',
      bay: null,
      gaps: [],
    };
  }
  const { frame, index: segIndex } = at;
  const wallLen = frame.wallLen;
  // Where this end sits on its own segment, and where the far end is.
  // `start` is NOT always the lower param: `buildRunSegments` records the
  // direction the run was drawn (its own sliver filter is
  // |endParam - startParam|), so a run drawn right-to-left along its wall
  // is stored with descending params — 9% of the corpus. Reading the sides
  // as fixed made every such end come out as an INVERTED range, which the
  // pinned test (hi - lo <= eps) then read as an end that could not move:
  // both handles drew muted and every drag clamped to nonsense.
  const moving = end === 'start' ? frame.startParam : frame.endParam;
  const other = end === 'start' ? frame.endParam : frame.startParam;
  const isLow = moving <= other;
  // Each side remembers what set it, so a drag that overshoots one side
  // can be told about THAT bound — not whichever was recorded last.
  let lo = frame.bounded ? 0 : -Infinity;
  let hi = frame.bounded ? wallLen : Infinity;
  let loBy = 'wall';
  let hiBy = 'wall';
  let loBay = null;
  let hiBay = null;
  if (isLow) {
    if (other - minSpan < hi) {
      hi = other - minSpan;
      hiBy = 'span';
    }
  } else if (other + minSpan > lo) {
    lo = other + minSpan;
    loBy = 'span';
  }
  // The side the end GROWS toward stops where another run starts. Measured
  // from where the end is now, so a run that already touches its neighbour
  // can still be shortened.
  const reach = room
    ? runEndNeighbourReach({
        room,
        stored,
        run,
        seg: at.seg,
        frame,
        moving,
        toward: isLow ? -1 : 1,
      })
    : null;
  if (reach != null) {
    if (isLow && moving - reach > lo) {
      lo = moving - reach;
      loBy = 'run';
    } else if (!isLow && moving + reach < hi) {
      hi = moving + reach;
      hiBy = 'run';
    }
  }
  for (const b of applianceBays(run, segIndex)) {
    if (b.unplaced) continue;
    const bLo = b.anchorParam - b.width / 2;
    const bHi = b.anchorParam + b.width / 2;
    if (isLow && bLo < hi) {
      hi = bLo;
      hiBy = 'bay';
      hiBay = b;
    } else if (!isLow && bHi > lo) {
      lo = bHi;
      loBy = 'bay';
      loBay = b;
    }
  }
  // Another run on this wall (`runNeighbourSpans`): the end stops where the
  // neighbour starts. The fill packs a run's own span and nothing else, so a
  // run dragged into its neighbour re-solves cabinets straight into its —
  // and a wall can carry two runs now, its own and a shallow storage run
  // beside it (`shallowRuns.js`). One already overlapping says nothing.
  const runLo = Math.min(moving, other);
  const runHi = Math.max(moving, other);
  for (const n of neighbours[segIndex] || []) {
    if (n.hi > runLo + NEIGHBOUR_EPS && n.lo < runHi - NEIGHBOUR_EPS) continue;
    if (isLow && n.hi <= moving + NEIGHBOUR_EPS && n.hi > lo) {
      lo = n.hi;
      loBy = 'run';
    } else if (!isLow && n.lo >= moving - NEIGHBOUR_EPS && n.lo < hi) {
      hi = n.lo;
      hiBy = 'run';
    }
  }
  // Where the fill can stand a cabinet, asked with the end pushed to the
  // furthest the bounds above allow. An end SAVED past the last covered
  // stretch (a generated layout's end runs on into a corner clearance; a run
  // saved before this rule) is held there too: its handle is drawn where the
  // cabinets stop, and the next drag or click saves that end.
  let gaps = [];
  const extendTo = isLow ? lo : hi;
  const cover =
    room && stored && openings && frame.bounded && Number.isFinite(extendTo)
      ? runEndFillCover({
          room,
          stored,
          run,
          seg: at.seg,
          segIndex,
          frame,
          end,
          extendTo,
          openings,
          push: () => {
            const edited = runEndEdit(stored, run, end, extendTo);
            return edited ? rehydrateRun(edited, room) : null;
          },
        })
      : null;
  if (cover) {
    if (isLow) {
      const reach = cover[0][0];
      if (reach > lo + FILL_EPS) {
        lo = Math.min(reach, hi);
        loBy = 'fill';
        loBay = null;
      }
    } else {
      const reach = cover[cover.length - 1][1];
      if (reach < hi - FILL_EPS) {
        hi = Math.max(reach, lo);
        hiBy = 'fill';
        hiBay = null;
      }
    }
    gaps = fillGaps(cover, lo, hi);
  }
  // The bound that matters when the range has NO width (a pinned end): a
  // bay if either side is one, else the span, else another run, else the
  // fill, else the wall.
  const bay = hiBay || loBay;
  const boundBy = bay
    ? 'bay'
    : hiBy === 'span' || loBy === 'span'
      ? 'span'
      : hiBy === 'run' || loBy === 'run'
        ? 'run'
        : hiBy === 'fill' || loBy === 'fill'
          ? 'fill'
          : 'wall';
  return { lo, hi, loBy, hiBy, loBay, hiBay, boundBy, bay, gaps };
}

// Below this the fill's reach IS the bound already set (a wall end the last
// cabinet sits flush with), not a new one.
const FILL_EPS = 1e-4;

/**
 * The uncovered stretches strictly inside `[lo, hi]`, as `[a, b]` pairs —
 * between two covered stretches, and before the first one when the range
 * starts short of it.
 */
function fillGaps(cover, lo, hi) {
  const gaps = [];
  for (let i = 0; i < cover.length; i++) {
    const a = cover[i][1];
    const b = i + 1 < cover.length ? cover[i + 1][0] : hi;
    if (b - a > FILL_EPS && a < hi && b > lo) {
      gaps.push([Math.max(a, lo), Math.min(b, hi)]);
    }
  }
  const head = cover[0][0];
  if (head - lo > FILL_EPS && head > lo) gaps.unshift([lo, Math.min(head, hi)]);
  return gaps;
}

/**
 * The index a rehydrated segment has in the STORED run. A free-standing
 * segment carries the one rehydrate stamped on it; a wall-backed one is
 * found by its wall.
 */
function storedSegmentIndex(stored, seg) {
  if (Number.isFinite(seg?._storedIdx)) return seg._storedIdx;
  const idx = (stored?.segments || []).findIndex(
    (s) => !s.freestanding && s.wallId === seg?.wall?.id
  );
  return idx >= 0 ? idx : 0;
}

/**
 * Forget the POSITIONED overrides a shortened segment no longer reaches.
 *
 * Moving a run's end does not change what any bay's `anchorParam` MEANS — the
 * wall frame is untouched — but it does change what the run can still honour,
 * and a positioned override is a remembered gesture ("this bay's edge goes
 * HERE") rather than a measurement. Dragged past, it does not simply go away:
 * `fillSegmentZone` stops seeing it, the cabinet vanishes, and the record
 * stays in the document. Drag the end back out later and it returns, now too
 * big for where it lands — which is how lengthening a run came to make
 * cabinets disappear.
 *
 * A width override that is NOT positioned is left alone: it never claimed a
 * place, so the packer can still sequence it wherever the run now reaches.
 *
 * Wall-backed segments only. A free-standing leg measures its bays from a
 * virtual wall synthesized out of its own two endpoints, so its span is not a
 * pair of params to compare against and the caller has already re-anchored
 * every bay by the origin's shift. `overridesForZone` is the net there, as it
 * is for any other way a run comes to be shorter than its overrides assume.
 */
export function dropStrandedOverrides(stored, idx) {
  const overrides = overridesForRun(stored);
  if (!overrides.length) return stored;
  const seg = (stored.segments || [])[idx];
  if (!seg || seg.freestanding) return stored;
  const lo = Math.min(seg.startParam, seg.endParam);
  const hi = Math.max(seg.startParam, seg.endParam);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return stored;
  const kept = overrides.filter((o) => {
    if (!isPositionedOverride(o) || o.segmentIndex !== idx) return true;
    const half = o.width / 2;
    return (
      o.anchorParam - half >= lo - REACH_EPS &&
      o.anchorParam + half <= hi + REACH_EPS
    );
  });
  return kept.length === overrides.length
    ? stored
    : { ...stored, bayOverrides: kept };
}

/**
 * Apply a dragged run end to the STORED run: the segment's new terminal
 * param, or — for a free-standing leg — its new terminal POINT.
 *
 * A wall-backed segment measures its params in the WALL's frame, so moving
 * one end leaves every bay's `anchorParam` meaning exactly what it did. A
 * free-standing leg has no such frame: the fill measures its bays from a
 * virtual wall synthesized out of the leg's own two endpoints, so moving
 * the endpoint that anchors that wall also moves the origin the bays are
 * counted from. They are re-anchored by that shift, so the island's sink
 * stays where it stands while the counter grows past it.
 *
 * @returns {Object|null} the edited stored run
 */
export function runEndEdit(stored, run, end, param) {
  const at = runEndSegment(run, end);
  if (!at || !stored || !Number.isFinite(param)) return null;
  const { seg, frame } = at;
  const idx = storedSegmentIndex(stored, seg);
  const storedSeg = (stored.segments || [])[idx];
  if (!storedSeg) return null;

  if (!seg.freestanding) {
    const segments = stored.segments.map((s, i) =>
      i !== idx
        ? s
        : end === 'start'
          ? { ...s, startParam: param }
          : { ...s, endParam: param }
    );
    return dropStrandedOverrides({ ...stored, segments }, idx);
  }

  // `end` names the field too: a free-standing segment stores its line as
  // `start` / `end` world points.
  const point = wallParamToSurfacePoint(frame.wall, param);
  if (!point) return null;
  const nextSeg = { ...storedSeg, [end]: { ...storedSeg[end], ...point } };
  const nextFrame = segmentFrame(nextSeg);
  if (!nextFrame) return null;
  const segments = stored.segments.map((s, i) => (i === idx ? nextSeg : s));
  // Where the OLD origin lands in the new frame: a bay at old param `a`
  // sits at `a + shift` once the origin has moved. Zero whenever the end
  // that moved was not the origin — then the bays are left alone.
  const shift = worldToWallParam(
    nextFrame.wall,
    frame.wall.alignment.bottomLeft
  );
  if (!Number.isFinite(shift) || Math.abs(shift) < 1e-9) {
    return { ...stored, segments };
  }
  const bays = baysForRun(stored).map((b) =>
    (b.segmentIndex ?? 0) === idx && Number.isFinite(b.anchorParam)
      ? { ...b, anchorParam: b.anchorParam + shift }
      : b
  );
  // The edit is expressed in BAYS from here on; the stale legacy list must
  // not shadow it on the next read (same rule as `editBayAnchor`).
  const { appliances: _legacy, ...rest } = stored;
  return dropStrandedOverrides({ ...rest, segments, bays }, idx);
}

/**
 * Translate a whole FREE-STANDING leg by a world delta — the island move.
 *
 * A leg is the only run geometry that can be picked up: a wall-backed segment
 * is pinned to its wall by `startParam`/`endParam` and has nowhere to go, so
 * this refuses one rather than inventing a meaning for it.
 *
 * Only `start` / `end` move. The bays' `anchorParam` are measured from the
 * leg's own synthesized origin, which travels with it, so every appliance and
 * cabinet rides along untouched — the same reason the row depths and the knee
 * are unaffected. Contrast `runEndEdit`, where one endpoint moves and the
 * origin shifts UNDER the bays, which is why that one has to re-anchor them.
 *
 * Not clamped to the room. A leg's ends are not clamped either, and the draw
 * tool places one anywhere on the floor — an island that cannot be dragged
 * across its own room would be the odd one out, and the cursor is the bound
 * in practice.
 *
 * @returns {Object|null} the edited stored run
 */
export function moveFreeSegment(stored, segIndex, delta) {
  const seg = (stored?.segments || [])[segIndex];
  if (!seg?.freestanding || !seg.start || !seg.end) return null;
  if (!Number.isFinite(delta?.x) || !Number.isFinite(delta?.z)) return null;
  const shift = (p) => ({ ...p, x: p.x + delta.x, z: p.z + delta.z });
  const segments = stored.segments.map((s, i) =>
    i === segIndex ? { ...s, start: shift(s.start), end: shift(s.end) } : s
  );
  return { ...stored, segments };
}

/**
 * Whether a leg may be picked up at all: a JOINED leg may not.
 *
 * An L-island's second leg and a peninsula arm meeting its wall run share an
 * endpoint with a neighbour, and translating one alone tears that corner open
 * — the fill would emit two runs of cabinetry that no longer meet. Moving a
 * multi-leg run is a different gesture (every leg together), not this one.
 */
export function freeSegmentIsDetached(run, segIndex) {
  const seg = (run?.segments || [])[segIndex];
  if (!seg?.freestanding || !seg.start || !seg.end) return false;
  const others = runSegments(run).filter((x) => x.index !== segIndex);
  const ends = [];
  for (const { seg: s, frame } of others) {
    const a = wallParamToSurfacePoint(frame.wall, frame.startParam);
    const b = wallParamToSurfacePoint(frame.wall, frame.endParam);
    if (a) ends.push(a);
    if (b) ends.push(b);
    if (s.freestanding) {
      if (s.start) ends.push(s.start);
      if (s.end) ends.push(s.end);
    }
  }
  const near = (p, q) => Math.hypot(p.x - q.x, p.z - q.z) < 1e-3;
  return !ends.some((e) => near(e, seg.start) || near(e, seg.end));
}

/**
 * `param` held to the end's range. An end let go in a stretch no cabinet can
 * stand in (a doorway, a low window) goes back to its NEAR edge, where the
 * cabinets on the run's side of it stop — never into the doorway, and never
 * to the far edge, which would end the run exactly where a cabinet it does
 * not include begins.
 *
 * Also where an end IS, as far as anyone can see: `runEndHandles` draws each
 * handle at its saved end held by this, so an end saved out in such a stretch
 * shows at the cabinets rather than in empty floor.
 */
export function clampRunEnd(run, end, param, opts = {}) {
  const range = runEndRange(run, end, opts);
  const p = Math.min(Math.max(param, range.lo), range.hi);
  const at = runEndSegment(run, end);
  if (!at || !range.gaps?.length) return p;
  const moving = end === 'start' ? at.frame.startParam : at.frame.endParam;
  const other = end === 'start' ? at.frame.endParam : at.frame.startParam;
  const growsUp = moving > other;
  for (const [a, b] of range.gaps) {
    if (p > a + FILL_EPS && p < b - FILL_EPS) return growsUp ? a : b;
    // The far edge itself is still in the doorway as far as the run goes.
    if (growsUp && Math.abs(p - b) <= FILL_EPS && b > a) return a;
    if (!growsUp && Math.abs(p - a) <= FILL_EPS && b > a) return b;
  }
  return p;
}

const FILL_REASON =
  'No cabinet fits any further along this wall — a door or window is in the way, or too little wall is left.';

const NEIGHBOUR_RUN_REASON =
  'The run already meets another cabinet run — it cannot be lengthened into it.';

/** A run end whose range has no width cannot move at all. */
const PINNED_EPS = 1e-6;

const humanizeBayType = (bay) => {
  try {
    const type = legacyApplianceFromBay(bay).type || 'appliance';
    return type
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[-_]/g, ' ')
      .toLowerCase();
  } catch {
    return 'appliance';
  }
};

/**
 * Why a drag PAST one side of the range went nowhere, in the words the
 * release will show. `side` is which bound the cursor overshot.
 *
 * The 'wall' case is the ROOM wall's end: a segment resolves to the raw
 * room wall (`buildSegmentWallResolver`, never kitchen-clipped), so a run
 * may be lengthened to the wall's corner and no further. (Before the
 * room-wall frame, a scan run's params were framed against the wall
 * clipped to the kitchen section, and "the wall's end" was that edge —
 * which is what made a run on a wall that kept going refuse to grow.)
 *
 * @param {{boundBy: string, bay: Object|null}} range
 * @param {'lo'|'hi'} side
 */
export function runEndLimitReason(range, side) {
  if (!range) return null;
  const by = side === 'hi' ? range.hiBy : range.loBy;
  const bay = side === 'hi' ? range.hiBay : range.loBay;
  if (by === 'bay' && bay) {
    return `Run ends at the ${humanizeBayType(bay)} — move or remove it to shorten the run.`;
  }
  if (by === 'span') {
    return 'The run is already as short as it can be.';
  }
  if (by === 'run') {
    return NEIGHBOUR_RUN_REASON;
  }
  if (by === 'fill') {
    return FILL_REASON;
  }
  return side === 'hi'
    ? 'The run already reaches the end of the wall — it cannot be lengthened past it.'
    : 'The run already starts at the end of the wall.';
}

/**
 * Why a pinned end cannot move, in the words the press will show.
 */
export function runEndPinReason(range) {
  if (!range) return null;
  if (range.boundBy === 'bay' && range.bay) {
    return `Run ends at the ${humanizeBayType(range.bay)} — move or remove it to shorten the run.`;
  }
  if (range.boundBy === 'span') {
    return 'The run is already as short as it can be.';
  }
  if (range.boundBy === 'run') {
    return NEIGHBOUR_RUN_REASON;
  }
  if (range.boundBy === 'fill') {
    return FILL_REASON;
  }
  return 'The run already spans the whole wall.';
}

/**
 * The world translation a bay's appliance takes when the bay slides along a
 * FREE-STANDING leg, or null when the segment is wall-backed (there the fill
 * re-seats the appliance at its solved param — `moveApplianceObjectsToBays`).
 *
 * An island has no such seat: `fillFreestandingSegment` returns no solved-bay
 * table and its segment carries no wall, so the leg's cabinets re-pack around
 * the new anchor and the appliance would stand still. Its motion is a pure
 * translation along the leg's own line — the twin of `legMoveOf`, one bay
 * instead of the whole leg.
 *
 * @param {Object} stored — the STORED run (bay `segmentIndex` is a stored index)
 * @param {Object} bay — the bay BEFORE the edit (its `anchorParam` is the from)
 * @param {number} param — the anchor after the edit
 * @param {Object} wall — the leg's virtual wall (`runSegmentWall`)
 * @returns {{segIndex: number, bayId: string, delta: {x: number, z: number}}|null}
 */
export function bayMoveOnFreeSegment(stored, bay, param, wall) {
  const segIndex = bay?.segmentIndex ?? 0;
  if (!stored?.segments?.[segIndex]?.freestanding) return null;
  const axes = wall ? getWallAxes(wall) : null;
  if (!axes || !Number.isFinite(param) || !Number.isFinite(bay?.anchorParam)) {
    return null;
  }
  const along = param - bay.anchorParam;
  if (!(Math.abs(along) > MOVE_EPS_M)) return null;
  return {
    segIndex,
    bayId: bay.id,
    delta: { x: axes.wallDir.x * along, z: axes.wallDir.z * along },
  };
}

/**
 * Ghost preview for an edited STORED run: the re-solved run objects plus the
 * appliance objects moved to their solved bays, and the ids of everything
 * the ghost replaces so the renderer hides them underneath it.
 *
 * `legMove` — `{ segIndex, delta: {x, z} }` — is the LEG gesture's own
 * translation, and `bayMove` (`{ segIndex, bayId, delta }`, from
 * `bayMoveOnFreeSegment`) is the BAY gesture's on a free-standing leg. Either
 * is required for an island to carry its appliances.
 * `moveApplianceObjectsToBays` seats each appliance at its solved param along
 * `seg.wall`, and a free-standing segment has neither a wall nor a solved-bay
 * table (#1537 M15), so without this an island move re-emitted the cabinets at
 * the new position, left the sink where it stood, and then HID the sink —
 * because it is a bay object — and redrew it unmoved. Omitted by the end and
 * bay gestures, which move no leg.
 *
 * @returns {{ objects, runId, hideIds: Set<string>, resolve, run, stored }}
 */
export function previewForRunEdit(
  room,
  storedEdited,
  openings = [],
  legMove = null,
  bayMove = null
) {
  const run = rehydrateRun(storedEdited, room);
  const resolve = materializeRunObjects(run, room, openings);
  const current = room?.objects || [];
  const hideIds = new Set(
    current.filter((o) => o?.runId === storedEdited.id).map((o) => o.id)
  );
  const solved = moveApplianceObjectsToBays(current, run, resolve);
  // The STORED run, not the rehydrated one: a `segIndex` here and a bay's
  // `segmentIndex` are stored indices, and rehydrate drops orphaned segments,
  // so the two disagree on a run that has one. `legMove` may be a LIST — a
  // corner drag slides every island section past the corner.
  const moves = legMove
    ? Array.isArray(legMove)
      ? legMove
      : [legMove]
    : bayMove
      ? [bayMove]
      : [];
  const carried = moves.reduce(
    (acc, move) => {
      const step = translateFreeSegmentAppliances(
        acc.objects,
        storedEdited,
        move.segIndex,
        move.delta,
        // A LEG move carries every bay on the leg; a BAY slide carries one.
        bayMove ? { bayId: bayMove.bayId } : {}
      );
      return { objects: step.objects, moved: [...acc.moved, ...step.moved] };
    },
    { objects: solved.objects, moved: [] }
  );
  const moved = {
    objects: carried.objects,
    moved: [...solved.moved, ...carried.moved],
  };
  const bayIds = new Set(applianceBaysAll(run).map((b) => b.id));
  const applianceIds = new Set(
    applianceBaysAll(run)
      .map((b) => b.applianceId)
      .filter(Boolean)
  );
  const movedObjects = [];
  for (const o of moved.objects) {
    if (!o) continue;
    // A piece the run built is the re-solve's to draw, and it is in
    // `resolve.objects` already. A sink base is one, AND it carries the
    // sink's bay, so it was drawn twice: at its new place and, unmoved, at
    // its old one — a second sink base under the dragged sink (2026-10-26).
    if (o.runId === storedEdited.id) continue;
    const isBayObject =
      (o.bayId && bayIds.has(o.bayId)) || applianceIds.has(o.id);
    const wasMoved = moved.moved.includes(o.id);
    if (isBayObject || wasMoved) {
      hideIds.add(o.id);
      movedObjects.push(o);
    }
  }
  // The ghost draws every run object and every bay object at its new spot;
  // originals of both are hidden.
  return {
    objects: [...resolve.objects, ...movedObjects],
    runId: storedEdited.id,
    hideIds,
    resolve,
    run,
    // The edited STORED run. `run` is rehydrated (segments resolved to walls
    // and filtered), which the free-standing datum cannot be re-derived from;
    // the overlays that must follow the drag read this instead.
    stored: storedEdited,
  };
}

function applianceBaysAll(run) {
  const out = [];
  for (let i = 0; i < (run.segments || []).length; i++)
    out.push(...applianceBays(run, i));
  return out;
}
